import { useEffect } from 'react'
import { useForm } from 'react-hook-form'

import { useManagementGetCompanies } from '@apps-common/apis/management/company'
import {
  MANAGEMENT_QUERY_KEY_GET_CONTACT,
  MANAGEMENT_QUERY_KEY_GET_CONTACTS,
  useManagementCreateContact,
  useManagementDeleteContact,
  useManagementGetContact,
  useManagementUpdateContact
} from '@apps-common/apis/management/contact'
import { ButtonRoundedSm, ButtonRoundSm } from '@apps-common/components/elements'
import { CheckboxInputSM, InputSM, SelectInputSM } from '@apps-common/components/elements/form'
import { ConfirmModal, ConfirmModalAction, ModalButton } from '@apps-common/components/modals'
import {
  ModalBody,
  ModalCloseButton,
  ModalContainer,
  ModalFooter,
  ModalHeader,
  ModalHeaderContainer
} from '@apps-common/components/modals/modal'
import { commonIcons } from '@apps-common/config/icons'
import { ModalDismiss } from '@apps-common/config/modal'
import { FORM_DEFAULT_REQUIRED } from '@apps-common/config/react-hook-form'
import { EMAIL_VALIDATION_REGEX, PHONE_VALIDATION_REGEX } from '@apps-common/config/regex'
import { queryClient } from '@apps-common/lib'
import { IContactModel } from '@apps-common/types/management/contact'

export const ContactModal = ({
  onDismiss,
  componentProps
}: {
  onDismiss: (data?: string | null | undefined | number, role?: string) => void
  componentProps: any
}) => {
  const { isLoading: cyIsLoading, isError: cyIsError, data: companies } = useManagementGetCompanies({ name: '' })

  const {
    isLoading,
    isError,
    data: contact
  } = useManagementGetContact({
    contactId: componentProps?.contactId || '',
    config: {
      enabled: !!componentProps?.contactId
    }
  })

  const {
    reset,
    register,
    setValue,
    getValues,
    handleSubmit,
    formState: { errors }
  } = useForm<IContactModel>({})

  const onChanged = () => {
    queryClient.invalidateQueries({ queryKey: MANAGEMENT_QUERY_KEY_GET_CONTACT() })
    queryClient.invalidateQueries({ queryKey: MANAGEMENT_QUERY_KEY_GET_CONTACTS() })
    onDismiss(null, ModalDismiss.Confirm)
  }

  const { isPending: createIsLoading, mutate: createContact } = useManagementCreateContact(true, () => onChanged())
  const { isPending: updateIsLoading, mutate: updateContact } = useManagementUpdateContact(true, () => onChanged())
  const { isPending: deleteIsLoading, mutate: deleteContact } = useManagementDeleteContact(true, () => onChanged())

  useEffect(() => {
    if (companies && contact) {
      reset(contact)
    }
  }, [contact, companies])

  const onSubmit = async (ctData: any) => {
    try {
      if (ctData.contactId) {
        updateContact({ model: getValues() as any })
      } else {
        createContact({ model: getValues() as any })
      }
    } catch (e: any) {
      // ignore, let mutation handle error
    }
  }

  const loading = isLoading || isError || cyIsLoading || cyIsError
  const updating = createIsLoading || updateIsLoading || deleteIsLoading

  return (
    <ModalContainer innerClasses='!min-h-[640px]'>
      <ModalHeaderContainer>
        <div className='flex items-center justify-between'>
          <ModalHeader subtitle={componentProps?.contactId ? 'Updating' : 'Creating'} title='Contact' subtitleClasses='capitalize' />
          <div className='flex items-center gap-[10px]'>
            {componentProps?.contactId && (
              <ModalButton
                component={ConfirmModal}
                disabled={loading || updating}
                classes=''
                componentProps={{
                  text: 'Delete',
                  description: 'Confirm you would like to delete this contact. This cannot be undone.',
                  action: ConfirmModalAction.Error
                }}
                confirmCallback={() => deleteContact({ contactId: componentProps?.contactId })}
              >
                <ButtonRoundSm type='div' className='action-error'>
                  <commonIcons.delete />
                </ButtonRoundSm>
              </ModalButton>
            )}
            <ModalCloseButton closeModal={() => onDismiss(null, ModalDismiss.Cancel)} />
          </div>
        </div>
      </ModalHeaderContainer>
      <ModalBody classes='relative !pt-[110px] !pb-[80px]'>
        {(!isLoading && isError) ||
          (!cyIsLoading && cyIsError && (
            <p className='mb-[10px] text-error text-sm'>Contact failed to load. Please close window and try again.</p>
          ))}
        <form noValidate id='contactModalForm' onSubmit={handleSubmit(onSubmit)}>
          <div>
            <SelectInputSM
              type='text'
              placeholder='Related company'
              label='Company'
              id='companyId'
              showLabel
              loading={loading}
              disabled={updating}
              registration={register('companyId', {
                required: FORM_DEFAULT_REQUIRED
              })}
              options={companies?.map((cy) => ({ label: cy.companyName, value: cy.companyId })) || []}
              errors={errors.companyId}
            />
            <InputSM
              type='text'
              placeholder='Name of contact'
              label='Contact name'
              id='name'
              showLabel
              loading={loading}
              disabled={updating}
              registration={register('name', {
                required: FORM_DEFAULT_REQUIRED
              })}
              errors={errors.name}
            />
            <InputSM
              type='text'
              placeholder='Phone number for contact'
              label='Phone number'
              id='phone'
              showLabel
              loading={loading}
              disabled={updating}
              registration={register('phone', {
                required: FORM_DEFAULT_REQUIRED,
                pattern: {
                  value: PHONE_VALIDATION_REGEX,
                  message: 'Invalid phone format'
                }
              })}
              errors={errors.phone}
            />
            <InputSM
              type='email'
              placeholder='Email for contact'
              label='Email'
              id='email'
              showLabel
              loading={loading}
              disabled={updating}
              registration={register('email', {
                required: FORM_DEFAULT_REQUIRED,
                pattern: {
                  value: EMAIL_VALIDATION_REGEX,
                  message: 'Invalid email format'
                }
              })}
              errors={errors.email}
            />
            <CheckboxInputSM
              label='Is the primary contact'
              id='primaryContact'
              loading={loading}
              disabled={updating}
              registration={register('primaryContact')}
              errors={errors.primaryContact}
            />
            <CheckboxInputSM
              label='Is a mail subscriber'
              id='mailSubscriber'
              loading={loading}
              disabled={updating}
              registration={register('mailSubscriber')}
              errors={errors.mailSubscriber}
            />
          </div>
        </form>
      </ModalBody>
      <ModalFooter>
        <div className='flex items-center justify-start gap-2'>
          <ButtonRoundedSm
            form='contactModalForm'
            type='submit'
            loading={loading || updating}
            text={contact ? 'Update' : 'Create'}
            className='action-success sm:grow-0'
          />
        </div>
      </ModalFooter>
    </ModalContainer>
  )
}
